import { useEffect, useMemo, useRef, useState } from 'react';
import { signOut, userEmail } from './auth';
import DriverEditor from './DriverEditor';
import { distanceMiles, isOnMap, loadPlaces, nearestPlace, Place } from './geo';
import MapView, { MapHandle } from './MapView';
import PlaceSearch from './PlaceSearch';
import { PARKED_COLOR, STATUSES, statusColor } from './status';
import type { Availability, Driver, Environment, Position } from './types';
import { useDrivers } from './useDrivers';

type Row = { driver: Driver, parked: boolean, placeLabel: string, miles?: number };
type Tab = 'active' | 'parked' | Availability;
type Draft = { truckNumber: string, position?: Position, placeLabel?: string };

const formatMiles = (miles: number) => `${miles < 10 ? miles.toFixed(1) : Math.round(miles)} mi`;
const initials = (email: string) => email.slice(0, 2).toUpperCase();

export default function App({ env }: { env: Environment }) {
    const { drivers, loaded, live, users, toasts, dismissToast, save, move, remove } = useDrivers(env);
    const [placesReady, setPlacesReady] = useState(false);
    const [tab, setTab] = useState<Tab>('active');
    const [filter, setFilter] = useState('');
    const [selected, setSelected] = useState<string>();
    const [reference, setReference] = useState<Place>();
    const [draft, setDraft] = useState<Draft>();
    const map = useRef<MapHandle>(null);
    const fitted = useRef(false);

    useEffect(() => {
        loadPlaces().then(() => setPlacesReady(true));
    }, []);

    const rows = useMemo<Row[]>(() => [...drivers.values()].map((driver) => {
        const place = nearestPlace(driver.position);
        const onMap = isOnMap(driver.position, place);
        return {
            driver,
            // Drivers parked the old way, by dragging the pin out to sea, count as parked too.
            parked: driver.parked ?? !onMap,
            placeLabel: place && onMap ? place.label : 'Off the map',
            miles: reference && distanceMiles(reference.position, driver.position),
        };
    }), [drivers, placesReady, reference]);

    const counts = useMemo(() => {
        const result: Record<string, number> = { active: 0, parked: 0 };
        for (const row of rows) {
            const key = row.parked ? 'parked' : 'active';
            result[key] += 1;
            if (!row.parked && row.driver.available) {
                result[row.driver.available] = (result[row.driver.available] || 0) + 1;
            }
        }
        return result;
    }, [rows]);

    const visible = useMemo(() => {
        const words = filter.toLowerCase().split(/\s+/).filter(Boolean);
        return rows
            .filter((row) => tab === 'parked' ? row.parked : !row.parked && (tab === 'active' || row.driver.available === tab))
            .filter((row) => {
                const text = `${row.driver.truckNumber} ${row.driver.vehicleDimensions || ''} ${row.driver.phoneNumber || ''} ${row.placeLabel}`.toLowerCase();
                return words.every((word) => text.includes(word));
            })
            .sort((a, b) => reference && tab !== 'parked'
                ? a.miles! - b.miles!
                : a.driver.truckNumber.localeCompare(b.driver.truckNumber, undefined, { numeric: true }));
    }, [rows, tab, filter, reference]);

    const pins = useMemo(() => visible.map(({ driver, parked }) => ({ driver, parked })), [visible]);

    // Start with every active driver in view.
    useEffect(() => {
        if (loaded && placesReady && !fitted.current) {
            fitted.current = true;
            map.current?.fit(rows.filter((row) => !row.parked).map((row) => row.driver.position));
        }
    }, [loaded, placesReady, rows]);

    useEffect(() => {
        if (selected) {
            document.querySelector(`[data-driver="${CSS.escape(selected)}"]`)?.scrollIntoView({ block: 'nearest' });
        }
    }, [selected]);

    const select = (row: Row) => {
        if (selected === row.driver.truckNumber) {
            setSelected(undefined);
            return;
        }
        setSelected(row.driver.truckNumber);
        if (!row.parked) {
            map.current?.flyTo(row.driver.position, 6);
        }
    };

    const moveTo = (row: Row, place: Place) => {
        move(row.driver.truckNumber, { position: place.position, parked: false }, `Moved ${row.driver.truckNumber} to ${place.label}`);
        map.current?.flyTo(place.position);
        if (row.parked) {
            setTab('active');
        }
    };

    const rename = async (row: Row, patch: object, newTruckNumber?: string) => {
        if (await save(row.driver.truckNumber, patch, newTruckNumber) && newTruckNumber) {
            setSelected(newTruckNumber.trim());
        }
    };

    const pickReference = (place: Place) => {
        setReference(place);
        setTab((current) => current === 'parked' ? 'active' : current);
        map.current?.flyTo(place.position, 7);
    };

    const draftName = draft?.truckNumber.trim() || '';
    const draftTaken = drivers.has(draftName);
    const createDraft = async () => {
        if (draft?.position && draftName && !draftTaken) {
            if (await save(draftName, { position: draft.position, available: 'AVAILABLE', parked: false })) {
                setDraft(undefined);
                setTab('active');
                setSelected(draftName);
            }
        }
    };

    const tabs: { value: Tab, label: string, color?: string }[] = [
        { value: 'active', label: 'On the map' },
        ...STATUSES.map((status) => ({ value: status.value, label: status.label, color: status.color })),
        { value: 'parked', label: 'Parked', color: PARKED_COLOR },
    ];

    return (
        <div className='app'>
            <aside className='sidebar'>
                <header>
                    <h1>Drivers</h1>
                    <button className='primary' onClick={() => { setDraft({ truckNumber: '' }); setSelected(undefined); }}>Add driver</button>
                </header>
                <input className='filter' placeholder='Filter by truck, name, phone, size or town' value={filter} onChange={(event) => setFilter(event.target.value)} />
                <nav className='tabs'>
                    {tabs.map((item) => <button key={item.value} className={tab === item.value ? 'on' : ''} onClick={() => setTab(item.value)}>
                        {item.color && <i style={{ background: item.color }} />}
                        {item.label} <b>{counts[item.value] || 0}</b>
                    </button>)}
                </nav>
                {reference && tab !== 'parked' && <div className='banner'>
                    <span>Nearest to <b>{reference.label}</b></span>
                    <button onClick={() => setReference(undefined)} aria-label='Clear place'>✕</button>
                </div>}
                {draft && <div className='draft'>
                    <label className='field'>
                        <span>Truck and driver</span>
                        <input autoFocus value={draft.truckNumber} placeholder='1234 Jane Doe' onChange={(event) => setDraft({ ...draft, truckNumber: event.target.value })} />
                    </label>
                    <div className='field'>
                        <span>Location · {draft.placeLabel || 'click the map, or search'}</span>
                        <PlaceSearch placeholder='ZIP, city or address' onPick={(place) => { setDraft({ ...draft, position: place.position, placeLabel: place.label }); map.current?.flyTo(place.position); }} />
                    </div>
                    {draftTaken && <div className='field-error'>A driver with this name already exists</div>}
                    <div className='editor-actions'>
                        <span className='spacer' />
                        <button onClick={() => setDraft(undefined)}>Cancel</button>
                        <button className='primary' disabled={!draft.position || !draftName || draftTaken} onClick={createDraft}>Add</button>
                    </div>
                </div>}
                <div className='list'>
                    {!loaded && <div className='empty'>Loading drivers…</div>}
                    {loaded && !visible.length && <div className='empty'>No drivers here</div>}
                    {visible.map((row) => {
                        const { driver } = row;
                        const open = driver.truckNumber === selected;
                        return (
                            <div key={driver.truckNumber} data-driver={driver.truckNumber} className={`row ${open ? 'open' : ''}`} onClick={() => select(row)}>
                                <div className='row-summary'>
                                    <i className='dot' style={{ background: row.parked ? PARKED_COLOR : statusColor(driver) }} />
                                    <div className='row-text'>
                                        <div className='row-title'>{driver.truckNumber}</div>
                                        <div className='row-sub'>
                                            {[row.parked ? 'Parked' : row.placeLabel, driver.vehicleDimensions, driver.phoneNumber].filter(Boolean).join(' · ')}
                                        </div>
                                    </div>
                                    {reference && !row.parked && <div className='row-miles'>{formatMiles(row.miles!)}</div>}
                                </div>
                                {open && <DriverEditor
                                    driver={driver}
                                    parked={row.parked}
                                    placeLabel={row.placeLabel}
                                    onSave={(patch, newTruckNumber) => rename(row, patch, newTruckNumber)}
                                    onMoveTo={(place) => moveTo(row, place)}
                                    onPark={() => { move(driver.truckNumber, { parked: true }, `Parked ${driver.truckNumber}`); setSelected(undefined); }}
                                    onDelete={() => { remove(driver.truckNumber); setSelected(undefined); }}
                                />}
                            </div>
                        );
                    })}
                </div>
            </aside>
            <main>
                <MapView
                    ref={map}
                    pins={pins}
                    selected={selected}
                    reference={reference?.position}
                    draft={draft?.position}
                    placing={!!draft}
                    onSelect={setSelected}
                    onMove={(truckNumber, position) => move(truckNumber, { position, parked: false }, `Moved ${truckNumber}`)}
                    onPlace={(position) => setDraft((current) => current && { ...current, position, placeLabel: nearestPlace(position)?.label || 'on the map' })}
                />
                <div className='topbar'>
                    <PlaceSearch placeholder='Find drivers near a ZIP, city or address' onPick={pickReference} />
                    <span className='spacer' />
                    <div className='presence'>
                        {users.filter((email) => email !== userEmail()).slice(0, 5).map((email) => <span key={email} className='avatar' title={`${email} is here`}>{initials(email)}</span>)}
                        <span className={`live ${live ? 'on' : ''}`} title={live ? 'Changes by others appear as they happen' : 'Connection lost, reconnecting'}>
                            <i />{live ? 'Live' : 'Reconnecting…'}
                        </span>
                        <button className='account' onClick={signOut} title={`Signed in as ${userEmail()}`}>Sign out</button>
                    </div>
                </div>
                {draft && <div className='hint'>Click the map to place the new driver</div>}
                <div className='toasts'>
                    {toasts.map((toast) => <div key={toast.id} className={`toast ${toast.error ? 'error' : ''}`}>
                        <span>{toast.message}</span>
                        {toast.undo && <button onClick={() => { toast.undo!(); dismissToast(toast.id); }}>Undo</button>}
                        <button onClick={() => dismissToast(toast.id)} aria-label='Dismiss'>✕</button>
                    </div>)}
                </div>
            </main>
        </div>
    );
}
